/* eslint-disable prettier/prettier */
import React, { useCallback, useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { ENV } from '@environment';
import { Formik, FormikValues, Form } from 'formik';
import { RootState } from 'store';
import * as yup from 'yup';

import BottomSheet from 'components/BottomSheet';
import Button from 'components/Buttons/Button';
import SimpleAlert from 'components/SimpleAlert';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { ResultPrice } from 'models/auto/price';
import { VehicleValidateResponse } from 'models/auto/vehicleValidate';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { Price } from 'models/price';
import ConfirmProfile from 'screens/components/Booking/ConfirmProfile';
import TravelJustify from 'screens/components/TravelJustify';
import { AddToCartEvent, amplitudelogEvent } from 'services/amplitudeService';
import { getCostCenters } from 'services/costCenterService';
import { BreakRulesReason } from 'services/travelBreakRulesReasonService';
import { Reason } from 'services/travelReasonService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { getFormattedPrice } from 'utils/currency';
import { daysCount } from 'utils/generic';

import {
  Checkout,
  ConfirmButton,
  ChurnContainer,
  SupportAvatar,
  ChurnTitle,
  ChurnDetail,
  CancelButton,
} from './styles';
import { PaymentCard } from 'models/paymentCard';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

interface AutoForm {
  justifyForBreakingRules: string;
  reason?: Reason;
  costCenter?: string;
  breakRulesReason?: BreakRulesReason;
  card?: PaymentCard;
}

interface Props {
  loading: boolean;
  price: Price;
  isPreApproved: boolean;
  isBestOption: boolean;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesReasonId?: string,
    card?:string
  ) => void;
  onClose: () => void;
  onContinue?: () => void;
  onContinueClose?: () => void;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  details: VehicleValidateResponse;
  alternativePrice?: ResultPrice;
}

const DetailsCheckout = ({
  loading,
  price,
  onConfirm,
  onClose,
  showChurning,
  setShowChurning,
  onContinue,
  onContinueClose,
  details,
  alternativePrice,
}: Props) => {
  const { t } = useStoreTranslation();
  const [showConfirmProfile, setShowConfirmProfile] = useState(false);
  const [showJustify, setShowJustify] = useState(false);
  const userAccount = useUserAccount();
  const [creditCardByUser, setCreditCardByUser] = useState(Boolean(userAccount.creditCardByUser));
  const { traveler, pickUpPoint, dropOffPoint, pickUpDate, dropOffDate, search } = useSelector(
    (state: RootState) => state.autos,
  );
  const isInternational = useMemo(() => pickUpPoint?.country !== dropOffPoint?.country, [pickUpPoint, dropOffPoint]);
  const history = useHistory();
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const companyConfiguration = useCompanyConfiguration();
  
  const initialValues: AutoForm = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? {
            justifyForBreakingRules: '',
            reason: undefined,
            costCenter: undefined,
            breakRulesReason: undefined,
          }
        : {
            justifyForBreakingRules: '',
            reason: undefined,
            breakRulesReason: undefined,
          },
    [costCenters],
  );

  const translateMessage = useCallback(
    (msg) => {
      const text = `rulesMessage.${msg}`;
      return t(text) !== text ? t(text) : msg;
    },
    [t],
  );

  useEffect(() => {
    getCostCenters(userAccount.companyId).then((res) => {
      if (res && res.data && res.data.totalResults > 0) {
        const centers = res.data.result.filter((c) => c.isActive);
        if (centers.length > 0) setCostCenters(centers);
      }
    });
  }, [t]);

  const handleSubmit = ({ justifyForBreakingRules, reason, costCenter, breakRulesReason,card }: AutoForm) => {
    if (reason) onConfirm(justifyForBreakingRules, reason.id, costCenter, breakRulesReason?.id, card?.cardId);
  };
  const isCreditCard = () => {
    if(creditCardByUser)
      return yup.object().required();
    else
      return yup.object().notRequired();
  }
  const validationSchema = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? yup.object().shape({
            justifyForBreakingRules: companyConfiguration?.menuView?.hideTravelReason ? yup.string().min(10).max(300).trim() : yup.string().min(10).max(300).trim().required(),
            reason: yup.object().required(),
            costCenter: yup.string().required(),
            card: isCreditCard(),
          })
        : yup.object().shape({
            justifyForBreakingRules: companyConfiguration?.menuView?.hideTravelReason ? yup.string().min(10).max(300).trim() : yup.string().min(10).max(300).trim().required(),
            reason: yup.object().required(),
            card: isCreditCard(),
          }),
    [costCenters, creditCardByUser],
  );

  const amplitudeAddToCartEvent: AddToCartEvent = {
    INDEX: 0,
    SOURCE: 'LIST',
    TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
    TRAVEL_FROM: pickUpPoint?.code ?? '',
    TRAVEL_TO: dropOffPoint?.code ?? '',
    TRAVEL_DESTINATION_TYPE:
      search?.distanceRestriction && search?.distanceRestriction === 'Domestic' ? 'NATIONAL' : 'INTERNATIONAL',
    TRAVEL_TRIP_TYPE: '',
    TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
    TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
    TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
    TRAVEL_TRIP_LENGTH:
      pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
    TRAVEL_PASSENGERS: '1',
    TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
    SEARCHID: search?.searchId ?? '',
    TRAVEL_PRODUCT_CODE: details?.vehicleOption?.company?.code ?? '',
    TRAVEL_PRODUCT_NAME: details?.vehicleOption?.company?.name ?? '',
    TRAVEL_SUB_PRODUCT: details?.vehicleOption?.vehicle?.model ?? '',
    TRAVEL_PRODUCT_CATEGORY: details?.vehicleOption?.vehicle?.vehicleCategory ?? '',
    TRAVEL_PRODUCT_PRICE:
      details?.vehicleOption?.rates &&
      details?.vehicleOption?.rates[0] &&
      details?.vehicleOption?.rates[0].totalPricePayNow
        ? `${details?.vehicleOption?.rates[0].totalPricePayNow}`
        : '',
    TRAVEL_PRODUCT_PROVIDER: details?.vehicleOption?.provider ?? '',
    TRAVEL_FARE_TYPE: '',
  };

  const sendAddToCartEvent = () => {
    amplitudelogEvent(amplitudeEvents.ADD_TO_CART, amplitudeAddToCartEvent, userAccount);
  };

  if (!userAccount) return null;

  const formattedPrice = useMemo(() => {
    if (price.total) {
      const { formatted } = getFormattedPrice({ value: price.total, currency: price.currencyCode, t });
      return formatted;
    }
    return undefined;
  }, [t, price]);

  const alternativeFormattedPrice = useMemo(() => {
    if (alternativePrice.total) {
      const { formatted } = getFormattedPrice({ value: alternativePrice.total, currency: alternativePrice.currencyCode, t });
      return formatted;
    }
    return undefined;
  }, [t, alternativePrice]);

  return (
    <>
      <Checkout>
        {userAccount?.bookingAllowed ? (
          <ConfirmButton
            onClick={() => {
              sendAddToCartEvent();
              setShowConfirmProfile(true);
              if (onContinue) onContinue();
            }}
            testId="auto-details-select-button"
          >
            <ConfirmButton.Label>{t('autosDetails.button')}</ConfirmButton.Label>
            <ConfirmButton.Label>{formattedPrice} {alternativeFormattedPrice && t('alternativePrice.or')} {alternativeFormattedPrice}</ConfirmButton.Label>
            
          </ConfirmButton>
        ) : (
          <SimpleAlert title={t('bookingNotAllowedAlert.title')} content={t('bookingNotAllowedAlert.content')} />
        )}
      </Checkout>

      <ConfirmProfile
        show={showConfirmProfile}
        productType={ProductType.Vehicle}
        onClose={() => {
          setShowConfirmProfile(false);
          if (onContinueClose) onContinueClose();
        }}
        onConfirm={() => setShowJustify(true)}
        user={
          traveler
            ? { ...traveler, nationality: traveler.country, userId: traveler.id || traveler.userId }
            : { userId: userAccount.id, ...userAccount }
        }
        isInternationalFlight={isInternational}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      >
        {({ isValid, handleSubmit: submit }: FormikValues) => (
          <Form>
            <TravelJustify
              show={showJustify}
              onClose={() => setShowJustify(false)}
              isValid={isValid}
              loading={loading}
              itineraryValue={price.total}
              currencyCode={price.currencyCode}
              onSubmit={submit}
              productType={ProductType.Vehicle}
              costCenters={costCenters}
              breakRulesMessage={translateMessage(details?.breakRulesMessage)}
              alternativePrice={alternativePrice.total}
              alternativePriceCurrencyCode={alternativePrice.currencyCode}              
              travelerId={(traveler ? traveler.id || traveler.userId : userAccount.id)}
              paymentExceptionProvider={details?.vehicleOption?.provider}
              onCreditCardByUserChange={setCreditCardByUser}
            />
          </Form>
        )}
      </Formik>

      <BottomSheet show={showChurning} modal noHeader onClose={() => {}}>
        <ChurnContainer>
          <SupportAvatar src="/corporate/images/support-avatar.png" />
          <ChurnTitle>{t('autosDetails.churn.title')}</ChurnTitle>
          <ChurnDetail>{t('autosDetails.churn.detail')}</ChurnDetail>

          <Button onClick={() => history.replace('/travelRequested')}>
            {t('autosDetails.churn.viewReservations')}
          </Button>
          <CancelButton
            onClick={() => {
              setShowChurning(false);
              onClose();
            }}
          >
            {t('autosDetails.churn.selectAnotherOption')}
          </CancelButton>
        </ChurnContainer>
      </BottomSheet>
    </>
  );
};

export default DetailsCheckout;
